/**
 * @fullvsl/app-shell/connection-store — per-subaccount Full Funnel connection
 * lifecycle store for the integrations screen (FUL-254).
 *
 * Two modes (mirrors VslStore.initLive):
 *  - **Live**: when the backend HTTP server is reachable, all connection
 *    operations proxy to the REST API in `api-client.ts` (which delegates to
 *    the encrypted `EncryptedConnectionManager` on the backend).
 *  - **Offline/mock**: a singleton `MockConnectionManager` (in-memory, hashes
 *    the secret + discards it) so the screen works on static hosts (Cloudflare
 *    Pages, `npm run dev`) without a backend.
 *
 * Security: the raw secret is submitted once and never retained. The live path
 * POSTs it to the backend vault (AES-256-GCM at rest); the mock path hashes it
 * immediately and stores only `RedactedCredential`. Only `RedactedCredential`
 * (mask + hash) is ever held in memory or rendered.
 */
import type {
  ConnectRequest,
  ConnectionManager,
  ConnectionTestRequest,
  ConnectionTestResult,
  CredentialAuditEvent,
  TenantConnection,
} from "../../contracts/src/contracts";
import { MockConnectionManager } from "../../contracts/src/tenant-connection";

/** Minimal lifecycle + audit view over the connection screen needs. */
export interface ConnectionStore {
  readonly live: boolean;
  list(): Promise<TenantConnection[]>;
  get(locationId: string): Promise<TenantConnection | null>;
  connect(req: ConnectRequest): Promise<TenantConnection>;
  test(locationId: string, opts?: ConnectionTestRequest): Promise<ConnectionTestResult>;
  revoke(locationId: string): Promise<boolean>;
  audit(locationId: string): Promise<CredentialAuditEvent[]>;
}

/** Wrap a live REST client (api-client.ts) as a ConnectionStore. */
class LiveConnectionStore implements ConnectionStore {
  readonly live = true;
  async list(): Promise<TenantConnection[]> {
    const { apiListConnections } = await import("./api-client");
    return apiListConnections();
  }
  async get(locationId: string): Promise<TenantConnection | null> {
    const { apiGetConnection } = await import("./api-client");
    try {
      return await apiGetConnection(locationId);
    } catch {
      return null;
    }
  }
  async connect(req: ConnectRequest): Promise<TenantConnection> {
    const { apiConnect } = await import("./api-client");
    return apiConnect(req);
  }
  async test(
    locationId: string,
    opts?: ConnectionTestRequest,
  ): Promise<ConnectionTestResult> {
    const { apiTestConnection } = await import("./api-client");
    return apiTestConnection(locationId, opts);
  }
  async revoke(locationId: string): Promise<boolean> {
    const { apiRevokeConnection } = await import("./api-client");
    return apiRevokeConnection(locationId);
  }
  async audit(locationId: string): Promise<CredentialAuditEvent[]> {
    const { apiConnectionAudit } = await import("./api-client");
    try {
      return await apiConnectionAudit(locationId);
    } catch {
      return [];
    }
  }
}

/** Wrap a ConnectionManager (mock or real) plus a no-op audit path as a store. */
class ManagerBackedStore implements ConnectionStore {
  readonly live = false;
  constructor(private readonly manager: ConnectionManager) {}
  list() {
    return this.manager.list();
  }
  get(locationId: string) {
    return this.manager.get(locationId);
  }
  connect(req: ConnectRequest) {
    return this.manager.connect(req);
  }
  test(locationId: string, opts?: ConnectionTestRequest) {
    return this.manager.test(locationId, opts);
  }
  revoke(locationId: string) {
    return this.manager.revoke(locationId);
  }
  /** The mock manager does not record audit events; expose an empty trail. */
  async audit(): Promise<CredentialAuditEvent[]> {
    return [];
  }
}

let activeStore: ConnectionStore | null = null;

/**
 * Initialize the connection store. Probes the live REST API first; on failure
 * degrades to a singleton in-memory MockConnectionManager so the screen remains
 * fully functional offline. Idempotent — the first resolution wins.
 */
export async function initConnectionStore(): Promise<ConnectionStore> {
  if (activeStore) return activeStore;
  try {
    const { detectApi } = await import("./api-client");
    const health = await detectApi();
    if (health.available) {
      // Confirm the connection routes are actually wired (not just /health).
      const { apiListConnections } = await import("./api-client");
      await apiListConnections();
      activeStore = new LiveConnectionStore();
      return activeStore;
    }
  } catch {
    // Backend not reachable / connection routes absent → mock mode.
  }
  activeStore = new ManagerBackedStore(new MockConnectionManager());
  return activeStore;
}

/** Direct accessor for the active store (null before init). */
export function getConnectionStore(): ConnectionStore | null {
  return activeStore;
}

/** Force a fresh mock store (tests). */
export function resetConnectionStore(mock = true): ConnectionStore {
  activeStore = mock
    ? new ManagerBackedStore(new MockConnectionManager())
    : new LiveConnectionStore();
  return activeStore;
}
